import { computed, defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";

import { IConversationUser } from "../../../vue-interfaces/messaging-service/i-message";
import { MessageService } from "../../../../services/message-service";
import { AnalyticsCategory, AnalyticsService } from "../../../../services/analytics.service";
import { IGetUserByName } from "../../../vue-interfaces/i-user";
import { markdownToHtml } from "../../../../common/vue-helpers/modifiers/markdown-to-html-modifier";
import { sanitizeMarkup } from "../../../../common/vue-helpers/modifiers/value-modifier";
import { formatMessageTime } from "../../../../common/vue-helpers/modifiers/format-message";
import { truncateString } from "../../../../common/vue-helpers/modifiers/format-message";
import { mapActions, mapState } from "pinia";
import { useConversationStore } from "../../../../pages-vue/MessageCenter/store/useConversationStore";
import { useUserStore } from "../../../../pages-vue/MessageCenter/store/useUsersStore";
import { useEditorStore } from "../../../../pages-vue/MessageCenter/store/useEditorStore";
import { IToolbarCustomButtons } from "../../../vue-interfaces/messaging-service/i-summer-editor";
import eventBus from "../../../../utilities/eventBus";

import SelectProfile from "../../../vue-dialogs/SelectProfile/SelectProfile.vue";

export default defineComponent({
    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    props: {
        isNewConversation: { type: Boolean, default: false }
    },

    data() {
        return {
            _messageService: null as MessageService,
            _analyticsService: null as AnalyticsService,
            editorMarkupRef: null as HTMLElement,
            replyMessageRef: null as HTMLElement,
            editorToolbarRef: null as HTMLElement,
            refsName: "chatEditorRef" as string,
            editor: null as JQuery<HTMLElement>,
            filteredUsers: [] as IGetUserByName[],
            uploadFile: null as FileList,
            showAttachmentLimitError: false as boolean,
            showDuplicateAttachmentError: false as boolean,
            shouldEnterSend: true as boolean,
            showStatusMsgs: true as boolean,
            customButtons: {} as IToolbarCustomButtons,
            dialogRef: null,
            isShowSelectProfile: false,
            taggedUsersList: [] as any[],
            sanitizeMarkup,
            markdownToHtml,
            formatMessageTime,
            truncateString
        };
    },

    computed: {
        ...mapState(useConversationStore, { activeConversation: "getActiveConversation" }),
        ...mapState(useUserStore, ["getMultipleUsers", "getDisplayStatusMsg"]),
        ...mapState(useEditorStore, ["editorMessage", "getFilesUI", "isMsgEmpty", "newChatUsers"]),

        chatStatusMsg() {
            if (this.activeConversation) {
                const status = this.getDisplayStatusMsg(this.activeConversation?.loginIds);
                if (status) {
                    this.showStatusMsgs = true;
                } else this.showStatusMsgs = false;
                return this.getDisplayStatusMsg(this.activeConversation?.loginIds);
            }
            this.showStatusMsgs = false;
            return null;
        },

        defaultOptions(): any {
            let styleOptions = ["bold", "italic", "style"] as const;
            let paraOptions = ["ul", "ol"] as const;
            let insertOptions = ["link"] as const;
            // TODO: Using Summernote.toolbarRef keeps throwing an error
            // even though the definition was followed
            let toolbarOptions: any = [
                ["style", styleOptions],
                ["para", paraOptions],
                ["insert", insertOptions],
                ["custom", ["attach", "important", "shareProfile"]],
                ["editMsgBtns", ["discardBtn", "sendBtn"]]
            ];
            let hintOptions: any = [
                {
                    mentions: this.filteredUsers,
                    match: /\B@(\w*)$/,
                    search: (keyword: any, callback: any) => {
                        this.getChatParticipantsList(keyword)
                            .then((users) => {
                                this.filteredUsers = users as any;
                                this.shouldEnterSend = false;
                                callback(users);
                            })
                            .catch((e) => {
                                console.log(e);
                            });
                    },
                    template: (item: IConversationUser) => {
                        if ((item as any) == "No Matches Found") {
                            return `<div class="text-center name-text loading-text">No Matches Found</div>`;
                        }
                        let nameInitials = "";
                        if (item.firstName) nameInitials += item.firstName.trim().charAt(0);
                        if (item.lastName) nameInitials += item.lastName.trim().charAt(0);
                        return `
                            <div class="m-1">
                                <div class="d-flex">
                                    <div class="ava-initials mr-1">
                                        <span>${nameInitials}</span>
                                    </div>
                                    <div>
                                    <div class="d-flex justify-content-between">
                                        <h6 class="name-text">
                                            ${item.firstName} ${item.lastName}
                                        <h6>
                                    </div>
                                    </div>
                                </div>
                            <div>`;
                    },
                    content: (item: IConversationUser) => {
                        // added extra <span> as the editor keeps running in the same span element when the user continues typing.
                        return $(`<span>`).html(
                            `<span id='${item.loginId}' data-actiontype='tagged-user' contenteditable='false' class="blue-text">${item.firstName} ${item.lastName}</span><span>&nbsp;</span>`
                        )[0];
                    }
                }
            ];
            return {
                toolbar: toolbarOptions,
                styleTags: ["h1", "h2", "h3", "h4", "h5", "h6"],
                placeholder: this.activeConversation?.hasUserLeftChat
                    ? "You can't send messages because you are not a member of the chat."
                    : "Start typing here...",
                height: 50,
                maxHeight: 50,
                focus: true,
                hint: hintOptions,
                toolbarContainer: `.${this.refsName}-editor-toolbar`,
                dialogsInBody: true,
                disableDragAndDrop: true,
                callbacks: {
                    onChange: (content: any) => {
                        this.setEditorMessage({ ...this.editorMessage, text: content });
                    },
                    onKeydown: (e: KeyboardEvent) => {
                        const currentNode = window.getSelection().anchorNode.parentNode;
                        let nodeName = currentNode.nodeName;
                        if (nodeName === "A") {
                            // code to handle case where user adds links inside list
                            nodeName =
                                $(window.getSelection().anchorNode.parentNode).parent()[0].nodeName === "LI"
                                    ? "LI"
                                    : "A";
                        }

                        if (["LI", "OL", "UL"].includes(nodeName)) {
                            this.shouldEnterSend = true;
                        } else {
                            if (e.key === "Enter" && this.disableSend) {
                                e.preventDefault();
                            } else if (e.key === "Enter" && this.shouldEnterSend && !e.shiftKey && !this.disableSend) {
                                this.send();
                                e.preventDefault();
                            }
                            this.shouldEnterSend =
                                !this.shouldEnterSend && ["ArrowUp", "ArrowDown", "ArrowRight"].includes(e.key)
                                    ? false
                                    : true;
                        }
                    },
                    onImageUpload: (files: any) => {
                        // This callback sends Files array instead of FileList,
                        // hence converting it to FileList for upload files logic
                        const dataTransfer = new DataTransfer();
                        files.forEach((file: any) => {
                            dataTransfer.items.add(file);
                        });
                        const fileList = dataTransfer.files;
                        this.uploadFilesChanged(fileList, true);
                    }
                }
            };
        },

        expandFilePreview() {
            return this.editorMessage.files.length;
        },

        expandProfilesPreview() {
            return this.editorMessage.allSelectedProfiles.length;
        },

        disableSend() {
            if (this.isNewConversation && this.newChatUsers?.length < 1) {
                return true;
            }
            return this.isMsgEmpty;
        }
    },

    watch: {
        "activeConversation.hasUserLeftChat": {
            handler() {
                this.reset();
            },
            deep: true
        }
    },

    created() {
        this._messageService = Container.instance.get(MessageService);
        this._analyticsService = Container.instance.get(AnalyticsService);

        if (this.activeConversation) {
            this.customButtons = {
                important: this.markAsImportantButton,
                shareProfile: this.shareProfile
            };
        } else {
            this.customButtons = {
                important: this.markAsImportantButton
            };
        }
    },

    mounted() {
        const editorRef = `${this.refsName}-textMarkup`;
        const replyRef = `${this.refsName}-replyMsg`;
        const toolbarRef = `${this.refsName}-toolbar`;
        this.editorMarkupRef = this.$refs?.[editorRef] as HTMLElement;
        this.replyMessageRef = this.$refs?.[replyRef] as HTMLElement;
        this.editorToolbarRef = this.$refs?.[toolbarRef] as HTMLElement;
        if (this.isNewConversation) {
            this.resetEditorMessage();
        }
        this.create();
    },

    methods: {
        ...mapActions(useEditorStore, [
            "setEditorMessage",
            "removeFile",
            "removeProfile",
            "formatAndSendMessage",
            "resetEditorMessage"
        ]),
        removeAttachment(fileName: string) {
            this.removeFile(fileName);
            this.showAttachmentLimitError = (this.editorMessage.files?.length || 0) > 25;
        },
        markAsImportantButton() {
            let ui = ($ as any).summernote.ui;
            // create button
            let button = ui.button({
                contents: `<i class="important-msg" />`,
                tooltip: this.editorMessage.isImportant ? "Mark as Standard" : "Mark as Important ",
                className: "mark-as-imp-btn",
                click: () => {
                    const tooltipText = this.editorMessage.isImportant ? "Mark as Important" : "Mark as Standard ";
                    const btn = $(".note-btn.mark-as-imp-btn");
                    btn.attr("data-original-title", tooltipText);
                    btn.attr("aria-label", tooltipText);
                    this.setEditorMessage({ ...this.editorMessage, isImportant: !this.editorMessage.isImportant });
                }
            });

            return button.render(); // return button as jquery object
        },

        shareProfile() {
            const ui = ($ as any).summernote.ui;
            const self = this;

            const button = ui.button({
                tooltip: "Share Profile",
                contents: `
                        <div
                            class="dropdown-toggle"
                            data-bs-toggle="dropdown"
                        >
                            <i class="share-user"></i>
                        </div>

                        <div class="dropdown-menu dropdown-variable p-0">
                            <a class="dropdown-item rounded-top"
                            href="#"
                            data-type="employee"
                            aria-label="Employee Profile">
                                <div class="d-flex justify-content-between">
                                    <div class="pe-4">
                                        <p class="mb-0 small fw-semibold">Employee Profile</p>
                                        <p class="mb-0 small text-muted">Share Contact</p>
                                    </div>
                                    <img src="${require("../../../../assets/images/chat/share-profile.svg")}"/>
                                </div>
                            </a>

                            <a class="dropdown-item rounded-bottom"
                            href="#"
                            data-type="patient"
                            aria-label="Patient Profile">
                                <div class="d-flex justify-content-between">
                                    <div class="pe-4">
                                        <p class="mb-0 small fw-semibold">Patient Profile</p>
                                        <p class="mb-0 small text-muted">Share Contact</p>
                                    </div>
                                    <img src="${require("../../../../assets/images/chat/share-profile.svg")}"/>
                                </div>
                            </a>
                        </div>
             `
            });

            const $btn = $(button.render());

            // Handle dropdown item click
            $btn.on("click", ".dropdown-item", function (e) {
                e.preventDefault();
                const type = $(this).data("type");
                type === "employee" ? self.employeeList() : self.patientList();
            });

            return $btn;
        },

        attachFileButton() {
            let ui = ($ as any).summernote.ui;

            // create button
            let button = ui.button({
                contents: `<i class="fas fa-paperclip" />`,
                tooltip: "Click to Attach a File",
                click: () => {
                    const toolbarRef = `${this.refsName}-uploadMarkup`;
                    $(this.$refs?.[toolbarRef]).trigger("click");
                }
            });

            return button.render(); // return button as jquery object
        },

        employeeList() {
            this.isShowSelectProfile = false;
            this.dialogRef = this.$dialog.open(SelectProfile, {
                props: {
                    modal: true,
                    showHeader: false,
                    style: {
                        width: "400px",
                        height: "500px"
                    }
                },
                data: {
                    name: "employee",
                    conversationId: this.activeConversation.id
                },
                onClose: (response: any) => {
                    if (response.data !== "cancel") {
                        this.setEditorMessage({
                            ...this.editorMessage,
                            allSelectedProfiles: [...this.editorMessage.allSelectedProfiles, ...response.data]
                        });
                    }
                }
            });
        },

        patientList() {
            this.isShowSelectProfile = false;
            this.dialogRef = this.$dialog.open(SelectProfile, {
                props: {
                    modal: true,
                    showHeader: false,
                    style: {
                        width: "400px",
                        height: "505px"
                    }
                },

                data: {
                    name: "patient",
                    conversationId: this.activeConversation.id
                },
                onClose: (response: any) => {
                    if (response.data !== "cancel") {
                        this.setEditorMessage({
                            ...this.editorMessage,
                            allSelectedProfiles: [...this.editorMessage.allSelectedProfiles, ...response.data]
                        });
                    }
                }
            });
        },

        toggleSelectProfile() {
            this.isShowSelectProfile = !this.isShowSelectProfile;
        },

        create() {
            let $markupRef = $(this.editorMarkupRef);
            let buttons;

            buttons = {
                buttons: { attach: this.attachFileButton, ...this.customButtons }
            };

            let editorOptions = Object.assign({}, buttons, this.defaultOptions);
            this.editor = ($markupRef as any).summernote(editorOptions);
            if (this.activeConversation?.hasUserLeftChat) {
                ($(this.editorMarkupRef) as any).summernote("disable");
            } else {
                // load draft if any or empty state
                this.editorMessage.files = this.editorMessage.files;
                ($(this.editorMarkupRef) as any).summernote("code", this.editorMessage.text);
            }
        },

        async send() {
            if (this.isNewConversation) {
                await this.formatAndSendMessage("", this.isNewConversation);
                eventBus.emit("updateNewConversation", false);
            }
            // condition for draft conversations
            else if (this.activeConversation?.isDraftConversation) {
                await this.formatAndSendMessage(this.activeConversation.id);
            } else {
                await this.formatAndSendMessage(this.activeConversation.id);
                ($(this.editorMarkupRef) as any).summernote("code", "");
            }
            this._analyticsService.logEvent({
                category: AnalyticsCategory.AxxessConnect,
                action: "Send Message"
            });
        },

        async getChatParticipantsList(filter: string) {
            const users = this.getMultipleUsers(this.activeConversation?.loginIds);
            const data = users.filter((user: IConversationUser) => {
                const fullName = user.firstName + " " + user.lastName;
                return fullName.toLowerCase().includes(filter.trim());
            });
            if (data.length > 0) return data;
            return ["No Matches Found"];
        },

        async uploadFilesChanged(event: any, isFilePasted: boolean = false) {
            if (isFilePasted) {
                this.uploadFile = event;
            } else {
                this.uploadFile = event.target.files;
            }
            const existingFileNames = new Set(this.editorMessage.files.map((file) => file.name));

            const uniqueFiles = Array.from(this.uploadFile).filter((file) => {
                if (existingFileNames.has(file.name)) {
                    // Show warning message if file is a duplicate
                    this.showDuplicateAttachmentError = true;
                    setTimeout(() => {
                        this.showDuplicateAttachmentError = false;
                    }, 5000);
                    return false;
                }
                existingFileNames.add(file.name);
                return true;
            });
            // Add only unique files to the existing files list
            const allFiles = [...this.editorMessage.files, ...uniqueFiles];
            this.setEditorMessage({ ...this.editorMessage, files: [...allFiles] });
            this.showAttachmentLimitError = allFiles.length > 25;
        },

        cancelReply() {
            this.setEditorMessage({ ...this.editorMessage, replyMessage: null });
        },

        closeStatusMsg() {
            this.showStatusMsgs = false;
        },

        reset() {
            ($(this.editorMarkupRef) as any).summernote("destroy");
            ($(this.editorToolbarRef) as any).empty();
            // initialize new instance
            this.create();
        }
    }
});
